
import { Component, Vue } from "vue-property-decorator";
import PreviewAnchorTabs from "./components/PreviewAnchorTabs.vue";
import PreviewIntroSection from "./components/PreviewIntroSection.vue";
import PreviewGoodsSection from "./components/PreviewGoodsSection.vue";
import PreviewRecordsSection from "./components/PreviewRecordsSection.vue";
import PreviewFooter from "./components/PreviewFooter.vue";
import ShareMessageToWeixin from "@/components/ShareMessageToWeixin/index.vue";
import {
  JIELONG_PREVIEW_STORAGE_KEY,
  JielongPreviewPayload,
  JielongPreviewTab,
  JIELONG_STATUS,
  getJielongDetail,
  normalizeJielongDetail,
  createJielong,
  updateJielong,
  serializeDetailListForApi,
  pickGoodsImage,
} from "@/api/jielong";
import { Getter } from "vuex-class";

const SECTION_IDS: Record<JielongPreviewTab, string> = {
  intro: "section-intro",
  goods: "section-goods",
  records: "section-records",
};

const TAB_BAR_HEIGHT = 44;

@Component({
  components: {
    PreviewAnchorTabs,
    PreviewIntroSection,
    PreviewGoodsSection,
    PreviewRecordsSection,
    PreviewFooter,
    ShareMessageToWeixin,
  },
})
export default class JielongPreviewPage extends Vue {
  payload: JielongPreviewPayload | null = null;
  loading = false;
  submitLoading = false;
  showAnchorTabs = false;
  activeTab: JielongPreviewTab = "intro";
  private observer: IntersectionObserver | null = null;
  private scrollLock = false;
  private resizeHandler: (() => void) | null = null;

  get isDetailMode (): boolean {
    return !!this.$route.query.id;
  }

  get pageTitle (): string {
    return this.isDetailMode ? "接龙详情" : "接龙预览";
  }

  /** 预览态主按钮：新建/草稿=立即发起，编辑=重新发起 */
  get previewSubmitText (): string {
    return this.payload?.editMode === "create" || this.payload?.activity?.isDraft === 1
      ? "立即发起"
      : "重新发起";
  }

  /** 仅详情态有活动 id，记录走独立接口；预览态为空不请求 */
  get recordsJoinActivityId (): string {
    if (!this.isDetailMode) return "";
    return String(this.$route.query.id || this.payload?.activity?.joinActivityId || "");
  }

  @Getter companyId!: string;
  @Getter logoUrl!: string;

  created (): void {
    if (this.isDetailMode) {
      this.loadDetail();
    } else {
      this.loadPreviewPayload();
    }
  }

  mounted (): void {
    this.$nextTick(() => {
      this.updateAnchorTabs();
      this.setupObserver();
    });
    this.resizeHandler = () => this.updateAnchorTabs();
    window.addEventListener("resize", this.resizeHandler);
  }

  beforeDestroy (): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
    }
  }

  loadPreviewPayload (): void {
    const raw = window.sessionStorage.getItem(JIELONG_PREVIEW_STORAGE_KEY);
    if (!raw) {
      this.$toast("预览数据不存在");
      this.$router.back();
      return;
    }
    try {
      this.payload = JSON.parse(raw) as JielongPreviewPayload;
    } catch (e) {
      this.$toast("预览数据解析失败");
      this.$router.back();
    }
  }

  loadDetail (): void {
    const id = String(this.$route.query.id || "");
    if (!id) {
      this.$toast("接龙不存在");
      this.$router.back();
      return;
    }
    this.loading = true;
    getJielongDetail({
      storeId: this.companyId,
      joinActivityId: id,
    })
      .then((res) => {
        this.payload = normalizeJielongDetail(res.data);
        this.$nextTick(() => {
          this.updateAnchorTabs();
          this.setupObserver();
        });
      })
      .catch(() => {
        this.$toast("接龙不存在");
        this.$router.back();
      })
      .finally(() => {
        this.loading = false;
      });
  }

  updateAnchorTabs (): void {
    this.$nextTick(() => {
      const el = this.$refs.scrollEl as HTMLElement | undefined;
      if (!el) return;
      this.showAnchorTabs = el.scrollHeight > el.clientHeight + 1;
      if (this.showAnchorTabs) {
        this.setupObserver();
      }
    });
  }

  setupObserver (): void {
    if (!this.showAnchorTabs) return;
    const root = this.$refs.scrollEl as HTMLElement | undefined;
    if (!root) return;

    if (this.observer) {
      this.observer.disconnect();
    }

    const visibleMap: Record<string, boolean> = {};
    const tabOrder: JielongPreviewTab[] = ["intro", "goods", "records"];

    this.observer = new IntersectionObserver(
      (entries) => {
        if (this.scrollLock) return;
        entries.forEach((entry) => {
          const id = entry.target.id;
          visibleMap[id] = entry.isIntersecting;
        });
        const active = tabOrder.find((tab) => visibleMap[SECTION_IDS[tab]]);
        if (active) {
          this.activeTab = active;
        }
      },
      {
        root,
        rootMargin: `-${TAB_BAR_HEIGHT}px 0px -55% 0px`,
        threshold: 0,
      }
    );

    tabOrder.forEach((tab) => {
      const section = document.getElementById(SECTION_IDS[tab]);
      if (section) this.observer!.observe(section);
    });
  }

  onTabChange (tab: JielongPreviewTab): void {
    this.activeTab = tab;
    const section = document.getElementById(SECTION_IDS[tab]);
    const container = this.$refs.scrollEl as HTMLElement | undefined;
    if (!section || !container) return;

    this.scrollLock = true;
    const top = section.offsetTop - (this.showAnchorTabs ? TAB_BAR_HEIGHT : 0);
    container.scrollTo({ top: Math.max(0, top), behavior: "smooth" });

    window.setTimeout(() => {
      this.scrollLock = false;
    }, 400);
  }

  onScroll (): void {
    // IntersectionObserver handles active tab; scroll hook reserved for extensions
  }

  onIntroExpandChange (): void {
    this.updateAnchorTabs();
  }

  onBack (): void {
    this.$router.back();
  }

  onSubmit (): void {
    if (this.isDetailMode || !this.payload || this.submitLoading) return;

    const { activity, goodsList, detailList, editMode } = this.payload;
    // 新建 / 草稿继续：走创建；编辑：走更新（不以 joinActivityId 判断，草稿也有 id）
    const isCreate = editMode === "create" || activity.isDraft === 1 || !editMode && !activity.joinActivityId;
    this.submitLoading = true;

    const done = () => {
      this.submitLoading = false;
    };
    const success = (msg: string) => {
      this.$toast(msg);
      window.sessionStorage.removeItem(JIELONG_PREVIEW_STORAGE_KEY);
      this.$store.commit("delKeepAlive", "NewJielong");
      // 栈为 列表 → 新建/编辑 → 预览，回退两层回到列表，避免返回又进新建页
      this.$router.go(-2);
    };
    const fail = (msg: string) => {
      this.$toast(msg);
    };

    if (isCreate) {
      createJielong({
        storeId: this.companyId,
        title: activity.title,
        isStorePickup: activity.isStorePickup,
        isHomeDelivery: activity.isHomeDelivery,
        startTime: activity.startTime,
        endTime: activity.endTime,
        arrivalTime: activity.arrivalTime,
        minOrderAmount: activity.minOrderAmount,
        minOrderQuantity: activity.minOrderQuantity,
        goodsList,
        detailList: serializeDetailListForApi(detailList),
        createType: 1,
      })
        .then(() => success("发起成功"))
        .catch(() => fail("发起失败，请重试"))
        .finally(done);
      return;
    }

    const joinActivityId = activity.joinActivityId;
    if (!joinActivityId) {
      fail("接龙信息不完整");
      done();
      return;
    }

    if (editMode === "limited" || activity.status === JIELONG_STATUS.ONGOING) {
      updateJielong({
        joinActivityId,
        storeId: this.companyId,
        updateType: 2,
        title: activity.title,
        minOrderAmount: activity.minOrderAmount,
        minOrderQuantity: activity.minOrderQuantity,
        extendTime: activity.extendTime || "",
        goodsList: goodsList.map((g) => ({
          joinActivityGoodsId: g.joinActivityGoodsId,
          goodsId: g.goodsId,
          stockQty: g.stockQty,
          showStock: g.showStock,
          minBuyQty: g.minBuyQty,
          maxBuyQty: g.maxBuyQty,
        })),
        detailList: serializeDetailListForApi(detailList),
      })
        .then(() => success("保存成功"))
        .catch(() => fail("保存失败，请重试"))
        .finally(done);
      return;
    }

    updateJielong({
      joinActivityId,
      storeId: this.companyId,
      updateType: 1,
      title: activity.title,
      isStorePickup: activity.isStorePickup,
      isHomeDelivery: activity.isHomeDelivery,
      startTime: activity.startTime,
      endTime: activity.endTime,
      arrivalTime: activity.arrivalTime,
      minOrderAmount: activity.minOrderAmount,
      minOrderQuantity: activity.minOrderQuantity,
      goodsList,
      detailList: serializeDetailListForApi(detailList),
    })
      .then(() => success("保存成功"))
      .catch(() => fail("保存失败，请重试"))
      .finally(done);
  }

  onShare (): void {
    if (!this.payload) return;
    const { activity, goodsList } = this.payload;
    const joinActivityId = String(
      this.$route.query.id || activity.joinActivityId || ""
    );
    if (!joinActivityId) {
      this.$toast("接龙尚未发起，暂不可分享");
      return;
    }

    const sftpUrl = this.$store.state.user.sftpUrl || "";
    const images = (goodsList || [])
      .map((g) => {
        const path = pickGoodsImage(g as any) || g.goodsImage || "";
        if (!path) return "";
        return path.startsWith("http") ? path : sftpUrl + path;
      })
      .filter(Boolean)
      .slice(0, 3);

    const prices = (goodsList || [])
      .map((g) => Number(g.joinPrice))
      .filter((n) => !Number.isNaN(n));
    let priceText = "";
    if (prices.length) {
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      priceText = min === max ? String(min) : `${min}-${max}`;
    }

    (this.$refs.ShareMessageToWeixinRef as any).showPopup({
      type: "activity",
      joinActivityId: joinActivityId,
      title: activity.title,
      createTime: activity.createTime,
      images,
      price: priceText,
      storeName: this.$store.state.user.info.storeName || "",
      storeLogo: this.logoUrl,
    });
  }

  onRefresh (): void {
    if (this.isDetailMode) {
      this.loadDetail();
      const recordsSection = this.$refs.recordsSection as { reload?: () => void } | undefined;
      recordsSection?.reload?.();
    }
  }
}
